import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bookingsApi } from '../api/bookingsApi';
import {
  BookingResponse,
  CreateBookingRequest,
  CreateBookingResponse,
} from '../types/booking.types';

/**
 * Hook for fetching user's bookings
 */
export const useMyBookings = () => {
  return useQuery<BookingResponse[], Error>({
    queryKey: ['bookings', 'my'],
    queryFn: () => bookingsApi.getMyBookings(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook for fetching a single booking by ID
 */
export const useBookingById = (id: string) => {
  return useQuery<BookingResponse, Error>({
    queryKey: ['booking', id],
    queryFn: () => bookingsApi.getById(id),
    enabled: !!id,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for creating a new booking
 */
export const useCreateBooking = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateBookingResponse, Error, CreateBookingRequest>({
    mutationFn: (data) => bookingsApi.create(data),
    onSuccess: () => {
      // Invalidate bookings list to refresh
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
};

/**
 * Hook for cancelling a booking
 */
export const useCancelBooking = () => {
  const queryClient = useQueryClient();

  return useMutation<BookingResponse, Error, string>({
    mutationFn: (id) => bookingsApi.cancel(id),
    onSuccess: (data) => {
      // Update the booking in cache
      queryClient.setQueryData(['booking', data.id], data);
      // Invalidate bookings list
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
};
